/**
 * EmailFiltersDashboard
 *
 * Power Dashboard giving a single-page view of every active email rule:
 *   - the match (email address / domain / subject) that selects an inbound email
 *   - the destination project + task it routes to
 *   - a quick toggle to disable/re-enable the rule
 *   - inline destination editing (change the project/task without re-typing the
 *     match value)
 *
 * Backed by the existing /api/email/rules endpoints; the GET already returns the
 * joined task_title / project_name so this view is read-only-cheap. Destination
 * edits go through PUT /email/rules/:id (which I fixed in this batch to resolve
 * UUIDs for target_task_id / target_project_id).
 *
 * Kept deliberately concise — the existing EmailRulesModal handles full
 * create/delete with match-type pickers etc. This dashboard is the at-a-glance
 * overview the user asked for.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Flex, HStack, Badge, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, Input, IconButton, useToast, Tooltip,
  Switch, FormControl, FormLabel,
} from '@chakra-ui/react';
import { FiMail, FiSave, FiX, FiSearch, FiRefreshCw, FiEdit2, FiChevronDown } from 'react-icons/fi';
import api from '../api';

const MATCH_TYPE_LABELS = {
  email_address: 'Email',
  from_address: 'From',
  from_domain: 'Domain',
  subject_contains: 'Subject',
};

const MATCH_TYPE_COLORS = {
  email_address: 'blue',
  from_address: 'blue',
  from_domain: 'purple',
  subject_contains: 'orange',
};

export default function EmailFiltersDashboard({ isOpen, onClose }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);   // rule.id we're changing destination on
  const [editChoices, setEditChoices] = useState([]); // [{project_id, task_id, label, is_done, is_complete}]
  const [editPick, setEditPick] = useState({ project_id: null, task_id: null });
  const [editLoading, setEditLoading] = useState(false);
  const [editSearch, setEditSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [appliedAt, setAppliedAt] = useState(null);   // last "Run rules now" timestamp
  const [applying, setApplying] = useState(false);
  const toast = useToast();

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/email/rules');
      setRules(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Failed to load email filters', status: 'error', duration: 2500 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isOpen) return;
    fetchRules();
    setFilter(''); setEditingId(null); setAppliedAt(null);
  }, [isOpen, fetchRules]);

  // Flat search across all visible columns
  const visibleRules = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rules.filter(r => {
      if (!showInactive && !r.is_active) return false;
      if (!q) return true;
      return (r.match_value || '').toLowerCase().includes(q)
          || (r.project_name || '').toLowerCase().includes(q)
          || (r.task_title || '').toLowerCase().includes(q)
          || (r.name || '').toLowerCase().includes(q);
    });
  }, [rules, filter, showInactive]);

  // ── Toggle is_active inline ────────────────────────────────────────────────
  const toggleActive = async (rule) => {
    setSavingId(rule.id);
    try {
      await api.put(`/email/rules/${rule.id}`, { is_active: !rule.is_active });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch (e) {
      toast({ title: 'Failed to update filter', status: 'error', duration: 2000 });
    } finally {
      setSavingId(null);
    }
  };

  // ── Begin destination edit: load company-less project/task list ───────────
  // We can't pre-filter by company since rules typically aren't company-bound.
  // Pull a lean project list and flatten to project+task pairs.
  const openDestinationEdit = async (rule) => {
    setEditingId(rule.id);
    setEditPick({ project_id: rule.target_project_id || null, task_id: rule.target_task_id || null });
    setEditSearch('');
    setEditChoices([]);
    setEditLoading(true);
    try {
      const { data } = await api.get('/projects');
      const items = [];
      for (const p of (data || [])) {
        if (p.is_done) continue;
        const tasks = p.tasks || [];
        if (tasks.length === 0) {
          items.push({
            project_id: p.id, task_id: null,
            label: p.project_name || '(Untitled project)',
            sub: '(no tasks)',
          });
          continue;
        }
        for (const t of tasks) {
          if (t.is_complete) continue;
          items.push({
            project_id: p.id, task_id: t.id,
            label: p.project_name || '(Untitled project)',
            sub: t.task_title || '(Task)',
          });
        }
      }
      setEditChoices(items);
    } catch (e) {
      toast({ title: 'Failed to load projects', status: 'error', duration: 2000 });
    } finally {
      setEditLoading(false);
    }
  };

  const filteredEditChoices = useMemo(() => {
    const q = editSearch.trim().toLowerCase();
    if (!q) return editChoices.slice(0, 80);
    return editChoices.filter(c =>
      (c.label || '').toLowerCase().includes(q) ||
      (c.sub || '').toLowerCase().includes(q)
    ).slice(0, 80);
  }, [editChoices, editSearch]);

  const saveDestination = async (choice) => {
    if (!editingId) return;
    setSavingId(editingId);
    try {
      await api.put(`/email/rules/${editingId}`, {
        target_project_id: choice.project_id,
        target_task_id: choice.task_id,
      });
      // Optimistic update — also patch in the joined display strings so the
      // row updates without a full refetch.
      setRules(prev => prev.map(r => r.id === editingId ? {
        ...r,
        target_project_id: choice.project_id,
        target_task_id: choice.task_id,
        project_name: choice.label,
        task_title: choice.task_id ? choice.sub : null,
      } : r));
      setEditingId(null);
      toast({ title: 'Destination updated', status: 'success', duration: 1500 });
    } catch (e) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    } finally {
      setSavingId(null);
    }
  };

  // ── Run all rules over historical unsorted emails ─────────────────────────
  // The /rules/apply-all endpoint re-runs every active rule against existing
  // unassigned email_messages — useful after creating or editing a rule.
  const applyAll = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/email/rules/apply-all');
      const applied = data?.applied ?? 0;
      const total = data?.total ?? 0;
      setAppliedAt(new Date());
      toast({
        title: `Applied ${applied} of ${total} unsorted emails`,
        status: 'success', duration: 2500,
      });
    } catch (e) {
      toast({ title: 'Apply failed', status: 'error', duration: 2500 });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" display="flex" alignItems="center" gap={2}>
          <FiMail color="#EE7203" />
          <Text>Email Filters</Text>
          <Badge colorScheme="gray" fontSize="9px" ml={1}>
            {rules.filter(r => r.is_active).length} active / {rules.length} total
          </Badge>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Controls */}
          <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
            <HStack spacing={2} flex={1} maxW="500px">
              <Box flex={1} position="relative">
                <Input size="sm" placeholder="Filter by email address, project, task…"
                  value={filter} onChange={e => setFilter(e.target.value)}
                  pl={8} />
                <Box position="absolute" left={2} top="50%" transform="translateY(-50%)" pointerEvents="none">
                  <FiSearch size={13} color="#A0AEC0" />
                </Box>
              </Box>
              <FormControl display="flex" alignItems="center" w="auto">
                <FormLabel htmlFor="show-inactive" mb="0" fontSize="11px" color="gray.600">
                  Show inactive
                </FormLabel>
                <Switch id="show-inactive" size="sm" isChecked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)} />
              </FormControl>
            </HStack>
            <HStack spacing={2}>
              <Button size="xs" leftIcon={<FiRefreshCw />} variant="outline"
                onClick={fetchRules} isLoading={loading}>
                Refresh
              </Button>
              <Tooltip label="Re-run every active rule against current unsorted inbox emails" fontSize="xs">
                <Button size="xs" leftIcon={<FiMail />} colorScheme="brand"
                  onClick={applyAll} isLoading={applying}>
                  Run rules now
                </Button>
              </Tooltip>
            </HStack>
          </Flex>

          {appliedAt && (
            <Text fontSize="10px" color="green.600" mb={2}>
              Last applied {appliedAt.toLocaleTimeString()}
            </Text>
          )}

          {/* Table */}
          {loading ? (
            <Flex justify="center" py={8}><Spinner size="md" /></Flex>
          ) : visibleRules.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text fontSize="sm" color="gray.500">
                {rules.length === 0
                  ? 'No email filters configured. Use Email Rules in settings to create one.'
                  : 'No filters match the current search.'}
              </Text>
            </Box>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th fontSize="9px" w="60px">Active</Th>
                  <Th fontSize="9px">Incoming match</Th>
                  <Th fontSize="9px">→ Project</Th>
                  <Th fontSize="9px">→ Task</Th>
                  <Th fontSize="9px" w="40px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibleRules.map(rule => (
                  <Tr key={rule.id} opacity={rule.is_active ? 1 : 0.55}>
                    <Td>
                      <Switch size="sm" isChecked={!!rule.is_active}
                        isDisabled={savingId === rule.id}
                        onChange={() => toggleActive(rule)} />
                    </Td>
                    <Td fontSize="12px">
                      <HStack spacing={2}>
                        <Badge fontSize="8px" colorScheme={MATCH_TYPE_COLORS[rule.match_type] || 'gray'}>
                          {MATCH_TYPE_LABELS[rule.match_type] || rule.match_type}
                        </Badge>
                        <Text fontFamily="mono" fontSize="11px" isTruncated maxW="240px"
                          title={rule.match_value}>
                          {rule.match_value || '(empty)'}
                        </Text>
                      </HStack>
                      {rule.name && rule.name !== rule.match_value && (
                        <Text fontSize="9px" color="gray.400" mt={0.5}>{rule.name}</Text>
                      )}
                    </Td>
                    <Td fontSize="12px">
                      {rule.project_name ? (
                        <Text isTruncated maxW="200px" title={rule.project_name}>
                          {rule.project_name}
                        </Text>
                      ) : (
                        <Text color="gray.400" fontStyle="italic">unassigned</Text>
                      )}
                    </Td>
                    <Td fontSize="12px">
                      {rule.task_title ? (
                        <Text isTruncated maxW="200px" title={rule.task_title}>
                          {rule.task_title}
                        </Text>
                      ) : (
                        <Text color="gray.400" fontStyle="italic">—</Text>
                      )}
                    </Td>
                    <Td>
                      <Tooltip label="Change destination" fontSize="xs">
                        <IconButton icon={<FiEdit2 />} size="xs" variant="ghost"
                          aria-label="Change destination"
                          onClick={() => openDestinationEdit(rule)} />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {/* Destination editor — inline panel below the table when an edit is active.
              Kept in the same modal rather than a nested one to avoid the Chakra
              focus-trap interaction the codebase has flagged before. */}
          {editingId && (
            <Box mt={4} p={3} bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md">
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="11px" fontWeight="700" color="orange.700">
                  Choose new destination
                </Text>
                <IconButton icon={<FiX />} size="xs" variant="ghost"
                  aria-label="Cancel"
                  onClick={() => setEditingId(null)} />
              </Flex>
              <Input size="sm" placeholder="Search projects / tasks…" mb={2}
                value={editSearch} onChange={e => setEditSearch(e.target.value)} autoFocus />
              {editLoading ? (
                <Flex justify="center" py={3}><Spinner size="sm" /></Flex>
              ) : (
                <Box maxH="240px" overflowY="auto" bg="white" border="1px" borderColor="gray.200" borderRadius="sm">
                  {filteredEditChoices.length === 0 ? (
                    <Text fontSize="11px" color="gray.500" textAlign="center" py={3}>
                      {editSearch ? 'No matches' : 'No active projects'}
                    </Text>
                  ) : filteredEditChoices.map((c, i) => {
                    const isCurrent = c.project_id === editPick.project_id && c.task_id === editPick.task_id;
                    return (
                      <Flex key={i} px={3} py={1.5} fontSize="12px"
                        cursor="pointer"
                        bg={isCurrent ? 'orange.100' : 'transparent'}
                        _hover={{ bg: 'gray.50' }}
                        borderBottom={i < filteredEditChoices.length - 1 ? '1px solid' : 'none'}
                        borderColor="gray.100"
                        align="center" justify="space-between"
                        onClick={() => saveDestination(c)}>
                        <Box>
                          <Text fontWeight={isCurrent ? 600 : 400}>{c.label}</Text>
                          <Text fontSize="10px" color="gray.500">↳ {c.sub}</Text>
                        </Box>
                        {isCurrent && <Badge colorScheme="orange" fontSize="8px">current</Badge>}
                      </Flex>
                    );
                  })}
                </Box>
              )}
              {editChoices.length > 80 && !editSearch && (
                <Text fontSize="9px" color="gray.400" mt={1}>
                  Showing first 80 — type to filter
                </Text>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
